import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaUserCircle, FaSave, FaArrowLeft, FaCamera, FaTrash } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import departmentService from '../services/departmentService';
import skillService from '../services/skillService';
import { API_ENDPOINTS } from '../config/api';
import '../styles/UserProfile.css';

const UserProfile = ({ user, onProfileUpdate }) => {
  const navigate = useNavigate();
  const token = sessionStorage.getItem('token');
  const isTechnician = user?.role === 'asset_technician';
  const fileInputRef = React.useRef(null);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || '',
    department: user?.department || '',
    lab: user?.lab || ''
  });
  const [saving, setSaving] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [skillOptions, setSkillOptions] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState(user?.skills || []);
  const [skillsSaving, setSkillsSaving] = useState(false);
  const [profilePic, setProfilePic] = useState(user?.profilePic || null);
  const [uploadingPic, setUploadingPic] = useState(false);

  useEffect(() => {
    fetchDepartments();
    if (isTechnician) {
      fetchSkills();
    }
  }, [isTechnician]);

  const fetchDepartments = async () => {
    try {
      const res = await departmentService.getAllDepartments();
      setDepartments(res.data || []);
    } catch (err) {
      console.error('Failed to fetch departments:', err);
    }
  };

  const fetchSkills = async () => {
    try {
      const response = await skillService.getAllSkills();
      if (response.success) {
        setSkillOptions(response.data.map(s => ({ value: s.name, label: s.label })));
      }
    } catch (err) {
      console.error('Error fetching skills:', err);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = { name: form.name };
      if (!isTechnician) {
        payload.department = form.department;
        payload.lab = form.lab;
      }
      const res = await axios.patch(
        API_ENDPOINTS.AUTH_PROFILE,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.success) {
        const updatedUser = { ...user, ...res.data.data };
        sessionStorage.setItem('user', JSON.stringify(updatedUser));
        onProfileUpdate(updatedUser);
        setEditing(false);
        toast.success('Profile updated successfully!');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSkillToggle = (skill) => {
    setSelectedSkills(prev =>
      prev.includes(skill) ? prev.filter(s => s !== skill) : [...prev, skill]
    );
  };

  const handleSkillsSave = async () => {
    if (selectedSkills.length === 0) {
      toast.error('Please select at least one skill');
      return;
    }
    try {
      setSkillsSaving(true);
      const res = await axios.patch(
        API_ENDPOINTS.AUTH_SKILLS,
        { skills: selectedSkills },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        const updatedUser = { ...user, skills: selectedSkills };
        sessionStorage.setItem('user', JSON.stringify(updatedUser));
        onProfileUpdate(updatedUser);
        toast.success('Skills updated successfully!');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to update skills');
    } finally {
      setSkillsSaving(false);
    }
  };

  const handleCancel = () => {
    setForm({
      name: user?.name || '',
      department: user?.department || '',
      lab: user?.lab || ''
    });
    setEditing(false);
  };

  const handleProfilePicChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    try {
      setUploadingPic(true);
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64String = event.target.result;
        const res = await axios.post(
          API_ENDPOINTS.AUTH_PROFILE_PIC,
          { profilePic: base64String },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (res.data.success) {
          setProfilePic(res.data.data.profilePic);
          const updatedUser = { ...user, profilePic: res.data.data.profilePic };
          sessionStorage.setItem('user', JSON.stringify(updatedUser));
          onProfileUpdate(updatedUser);
          toast.success('Profile picture updated successfully!');
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error(err);
      toast.error('Failed to upload profile picture');
    } finally {
      setUploadingPic(false);
    }
  };

  const handleDeleteProfilePic = async () => {
    if (!window.confirm('Are you sure you want to delete your profile picture?')) return;

    try {
      setUploadingPic(true);
      const res = await axios.delete(
        API_ENDPOINTS.AUTH_DELETE_PIC,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.success) {
        setProfilePic(null);
        const updatedUser = { ...user, profilePic: null };
        sessionStorage.setItem('user', JSON.stringify(updatedUser));
        onProfileUpdate(updatedUser);
        toast.success('Profile picture deleted successfully!');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete profile picture');
    } finally {
      setUploadingPic(false);
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      asset_incharge: 'Asset Incharge',
      asset_technician: 'Asset Technician',
      admin: ' Admin',
      asset_user: 'Asset User'
    };
    return labels[role] || role;
  };

  return (
    <div className="user-profile-page">
      <button className="back-btn" onClick={() => navigate(-1)}>
        <FaArrowLeft /> Back
      </button>

      <div className="profile-card">
        <div className="profile-card-header">
          <div className="profile-pic-container">
            {profilePic ? (
              <img src={profilePic} alt="Profile" className="profile-pic-image" />
            ) : (
              <FaUserCircle className="profile-big-avatar" />
            )}
            <div className="profile-pic-actions">
              <button
                className="pic-action-btn upload-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPic}
                title="Upload/Change profile picture"
              >
                <FaCamera /> Change
              </button>
              {profilePic && (
                <button
                  className="pic-action-btn delete-btn"
                  onClick={handleDeleteProfilePic}
                  disabled={uploadingPic}
                  title="Delete profile picture"
                >
                  <FaTrash /> Delete
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleProfilePicChange}
                style={{ display: 'none' }}
              />
            </div>
          </div>
          <div className="profile-card-title">
            <h2>{user?.name}</h2>
            <span className={`profile-role-badge role-${user?.role}`}>
              {getRoleLabel(user?.role)}
            </span>
          </div>
        </div>

        <div className="profile-card-body">
          {/* Email - always read-only */}
          <div className="profile-field">
            <label>Email</label>
            <div className="field-value">{user?.email}</div>
          </div>

          {/* Name */}
          <div className="profile-field">
            <label>Full Name</label>
            {editing ? (
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Enter your full name"
              />
            ) : (
              <div className="field-value">{user?.name || '—'}</div>
            )}
          </div>

          {/* Department - hidden for technicians, dropdown for others */}
          {!isTechnician && (
            <div className="profile-field">
              <label>Department</label>
              {editing ? (
                <select
                  value={form.department}
                  onChange={(e) => setForm({ ...form, department: e.target.value })}
                  className="profile-select"
                >
                  <option value="">Select department</option>
                  {departments.map(dept => (
                    <option key={dept._id} value={dept.label}>{dept.label}</option>
                  ))}
                </select>
              ) : (
                <div className="field-value">{user?.department || 'Not set'}</div>
              )}
            </div>
          )}

          {/* Lab / Location - hidden for technicians */}
          {!isTechnician && (
            <div className="profile-field">
              <label>Location / Lab</label>
              {editing ? (
                <input
                  type="text"
                  value={form.lab}
                  onChange={(e) => setForm({ ...form, lab: e.target.value })}
                  placeholder="e.g. Building A, Floor 3..."
                />
              ) : (
                <div className="field-value">{user?.lab || 'Not set'}</div>
              )}
            </div>
          )}

          {/* Role - always read-only */}
          <div className="profile-field">
            <label>Role</label>
            <div className="field-value">{getRoleLabel(user?.role)}</div>
          </div>
        </div>

        <div className="profile-card-footer">
          {!editing ? (
            <button className="edit-profile-btn" onClick={() => setEditing(true)}>
              Edit Profile
            </button>
          ) : (
            <div className="edit-actions">
              <button className="cancel-btn" onClick={handleCancel}>
                Cancel
              </button>
              <button className="save-btn" onClick={handleSave} disabled={saving}>
                <FaSave /> {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Skills Section - only for technicians */}
      {isTechnician && (
        <div className="profile-card skills-card">
          <div className="profile-card-body">
            <h3 className="skills-section-title">My Skills</h3>
            <p className="skills-section-desc">Select the skills you specialize in. This helps assign you to relevant issues.</p>

            <div className="skills-grid-profile">
              {skillOptions.map(skill => (
                <div
                  key={skill.value}
                  className={`skill-card-profile ${selectedSkills.includes(skill.value) ? 'selected' : ''}`}
                  onClick={() => handleSkillToggle(skill.value)}
                >
                  <input
                    type="checkbox"
                    checked={selectedSkills.includes(skill.value)}
                    onChange={() => handleSkillToggle(skill.value)}
                  />
                  <span>{skill.label}</span>
                </div>
              ))}
            </div>

            {selectedSkills.length > 0 && (
              <div className="selected-skills-summary">
                <strong>Selected ({selectedSkills.length}):</strong>
                <div className="skills-list">
                  {selectedSkills.map((skill, idx) => {
                    const label = skillOptions.find(s => s.value === skill)?.label || skill;
                    return <span key={idx} className="skill-chip">{label}</span>;
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="profile-card-footer">
            <button className="save-btn" onClick={handleSkillsSave} disabled={skillsSaving}>
              <FaSave /> {skillsSaving ? 'Saving...' : 'Update Skills'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfile;
